import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeftRight, CheckCircle2, XCircle, Clock, AlertTriangle, Plus, Loader2 } from "lucide-react";

const STATUS_META = {
  pending_counterparty: { label: "Waiting on colleague", tone: "amber",  icon: Clock },
  pending_admin:        { label: "Waiting on admin",     tone: "blue",   icon: Clock },
  approved:             { label: "Approved",              tone: "green",  icon: CheckCircle2 },
  declined:             { label: "Declined by admin",     tone: "red",    icon: XCircle },
  declined_by_counterparty: { label: "Declined by colleague", tone: "red", icon: XCircle },
  cancelled:            { label: "You cancelled",         tone: "slate",  icon: XCircle },
  auto_cancelled:       { label: "Auto-cancelled",        tone: "slate",  icon: AlertTriangle },
  expired:              { label: "Expired",                tone: "slate",  icon: AlertTriangle },
};

const TONE_CLS = {
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  blue:  "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFDBFE]",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function SwapsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ mine: [], awaiting_my_response: [], pending_admin: [] });
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data: body } = await api.get("/swaps");
      setData(body);
    } catch (e) { /* swallow */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    refresh();
  }, []);

  const isAdmin = user.role === "company_admin" || user.role === "team_admin";

  return (
    <div className="space-y-6" data-testid="swaps-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Shift swaps</div>
          <h1 className="font-display text-4xl mt-1">Swap a shift with a teammate</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Trade an upcoming published shift with someone in your team. Times, breaks, assignments and notes move together. Your admin needs to give the final OK before either roster changes.
          </p>
        </div>
        {user.employee_id && (
          <Button
            onClick={() => setRequestOpen(true)}
            data-testid="swap-new-btn"
            className="gap-2 bg-[#2563EB] hover:bg-[#1D4ED8]"
          >
            <Plus className="h-4 w-4"/> Request a swap
          </Button>
        )}
      </div>

      {loading ? (
        <Skel />
      ) : (
        <div className="space-y-8">
          {data.awaiting_my_response.length > 0 && (
            <Section
              title="Awaiting your response"
              testId="section-awaiting"
              empty="You have no swap requests waiting on you."
              swaps={data.awaiting_my_response}
              renderActions={(swap) => <CounterpartyActions swap={swap} onChanged={refresh}/>}
              perspective="counterparty"
            />
          )}

          {isAdmin && data.pending_admin.length > 0 && (
            <Section
              title="Pending admin approval"
              testId="section-admin"
              empty="No swaps are waiting for your sign-off."
              swaps={data.pending_admin}
              renderActions={(swap) => <AdminActions swap={swap} onChanged={refresh}/>}
              perspective="admin"
            />
          )}

          <Section
            title="My swap requests"
            testId="section-mine"
            empty="You have not requested any swaps yet."
            swaps={data.mine}
            renderActions={(swap) =>
              ["pending_counterparty", "pending_admin"].includes(swap.status) ? (
                <CancelButton swap={swap} onChanged={refresh}/>
              ) : null
            }
            perspective="requester"
          />
        </div>
      )}

      <RequestSwapDialog
        open={requestOpen}
        onOpenChange={(v) => setRequestOpen(v)}
        onCreated={async () => { setRequestOpen(false); await refresh(); }}
      />
    </div>
  );
}

function Skel() {
  return <div className="h-24 rounded-lg bg-slate-50 animate-pulse"/>;
}

function Section({ title, swaps, empty, testId, renderActions, perspective }) {
  return (
    <div data-testid={testId}>
      <h2 className="font-display text-2xl mb-3">{title}</h2>
      {swaps.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg p-5 text-sm text-[#4B5773]">{empty}</div>
      ) : (
        <div className="space-y-3">
          {swaps.map(s => (
            <SwapRow key={s.swap_id} swap={s} actions={renderActions?.(s)} perspective={perspective}/>
          ))}
        </div>
      )}
    </div>
  );
}

function SwapRow({ swap, actions, perspective }) {
  const meta = STATUS_META[swap.status] || { label: swap.status, tone: "slate", icon: Clock };
  const Icon = meta.icon;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5"
         data-testid={`swap-row-${swap.swap_id}`}
         data-swap-status={swap.status}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#4B5773]">
            {perspective === "counterparty" ? (
              <><strong>{swap.requester.name}</strong> would like to swap with you.</>
            ) : perspective === "admin" ? (
              <><strong>{swap.requester.name}</strong> ↔ <strong>{swap.counterparty.name}</strong></>
            ) : (
              <>You requested a swap with <strong>{swap.counterparty.name}</strong>.</>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <ShiftChip side="left" label="You would work" shift={
              perspective === "counterparty" ? swap.requester : swap.requester
            }/>
            <ArrowLeftRight className="h-4 w-4 mt-2 text-[#4B5773] shrink-0"/>
            <ShiftChip side="right" label="They would work" shift={
              perspective === "counterparty" ? swap.counterparty : swap.counterparty
            }/>
          </div>
          {swap.reason && (
            <p className="text-xs text-[#4B5773] mt-2"><em>Note:</em> {swap.reason}</p>
          )}
          {swap.admin_notes && (
            <p className="text-xs text-[#4B5773] mt-1"><em>Admin note:</em> {swap.admin_notes}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span
            data-testid={`swap-status-${swap.swap_id}`}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${TONE_CLS[meta.tone]}`}>
            <Icon className="h-3.5 w-3.5"/> {meta.label}
          </span>
          {actions}
        </div>
      </div>
    </div>
  );
}

function ShiftChip({ shift }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
      <div className="text-[11px] text-[#4B5773] uppercase tracking-wider">{shift.name}</div>
      <div className="text-sm font-medium text-[#0B1A36]">
        {fmtDate(shift.work_date)} · {shift.start_time}-{shift.end_time}
        {shift.break_minutes ? ` · ${shift.break_minutes}m break` : ""}
      </div>
    </div>
  );
}

function CounterpartyActions({ swap, onChanged }) {
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/swaps/${swap.swap_id}/${kind}`);
      toast.success(kind === "accept" ? "Swap accepted - your admin will confirm next." : "Swap declined.");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not update swap.");
    } finally { setBusy(null); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" disabled={!!busy}
        data-testid={`swap-${swap.swap_id}-accept`}
        onClick={() => act("accept")}
        className="bg-[#16A34A] hover:bg-[#15803D] text-white gap-1.5 min-h-[44px]">
        {busy === "accept" ? <Loader2 className="h-4 w-4 animate-spin"/> : <CheckCircle2 className="h-4 w-4"/>}
        Accept
      </Button>
      <Button size="sm" variant="outline" disabled={!!busy}
        data-testid={`swap-${swap.swap_id}-decline`}
        onClick={() => act("decline")}
        className="text-red-700 border-red-200 hover:bg-red-50 gap-1.5 min-h-[44px]">
        {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin"/> : <XCircle className="h-4 w-4"/>}
        Decline
      </Button>
    </div>
  );
}

function AdminActions({ swap, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState("");
  const decide = async (decision) => {
    setBusy(decision);
    try {
      await api.post(`/swaps/${swap.swap_id}/decision`, { decision, admin_notes: notes || undefined });
      toast.success(decision === "approved" ? "Swap approved - rosters updated." : "Swap declined.");
      onChanged?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not update swap.");
    } finally { setBusy(null); }
  };
  return (
    <div className="flex flex-col items-end gap-1.5">
      <Input
        placeholder="Optional note for both employees"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        maxLength={240}
        data-testid={`swap-${swap.swap_id}-admin-notes`}
        className="h-9 w-64"
      />
      <div className="flex gap-2">
        <Button size="sm" disabled={!!busy} onClick={() => decide("approved")}
          data-testid={`swap-${swap.swap_id}-approve`}
          className="bg-[#16A34A] hover:bg-[#15803D] text-white">
          {busy === "approved" ? "…" : "Approve"}
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => decide("declined")}
          data-testid={`swap-${swap.swap_id}-admin-decline`}
          className="text-red-700 border-red-200 hover:bg-red-50">
          {busy === "declined" ? "…" : "Decline"}
        </Button>
      </div>
    </div>
  );
}

function CancelButton({ swap, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm("Cancel this swap request? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/swaps/${swap.swap_id}/cancel`);
      toast.success("Swap cancelled.");
      onChanged?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not cancel.");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="ghost" disabled={busy}
      data-testid={`swap-${swap.swap_id}-cancel`}
      onClick={cancel}
      className="text-[#4B5773] hover:text-[#0B1A36] min-h-[44px]">
      {busy ? "…" : "Cancel request"}
    </Button>
  );
}

function RequestSwapDialog({ open, onOpenChange, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [mineId, setMineId] = useState("");
  const [collId, setCollId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setMineId(""); setCollId(""); setReason("");
    (async () => {
      try {
        const r = await api.get("/swaps/eligible-shifts");
        if (!alive) return;
        setMine(r.data.mine || []);
        setColleagues(r.data.colleagues || []);
      } catch { /* ignore - dialog can still be cancelled */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open]);

  const submit = async () => {
    if (!mineId || !collId) {
      toast.error("Pick one of your shifts and one of your colleague's shifts."); return;
    }
    setBusy(true);
    try {
      await api.post("/swaps", { requester_shift_id: mineId, counterparty_shift_id: collId, reason: reason || undefined });
      toast.success("Swap request sent.");
      onCreated?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not send swap.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="swap-request-dialog">
        <DialogHeader>
          <DialogTitle>Request a shift swap</DialogTitle>
          <DialogDescription>
            Pick one of your upcoming shifts and the colleague&apos;s shift you would like to swap with. Your admin needs to confirm before either roster changes.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-sm text-[#4B5773]">Loading shifts…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="eyebrow">My shift to give up</Label>
              <Select value={mineId} onValueChange={setMineId}>
                <SelectTrigger data-testid="swap-mine-select" className="mt-1.5 h-11">
                  <SelectValue placeholder={mine.length ? "Choose one of my shifts" : "You have no eligible shifts in the next 14 days"}/>
                </SelectTrigger>
                <SelectContent>
                  {mine.map(s => (
                    <SelectItem key={s.shift_id} value={s.shift_id}>
                      {fmtDate(s.work_date)} · {s.start_time}-{s.end_time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Colleague&apos;s shift to take instead</Label>
              <Select value={collId} onValueChange={setCollId}>
                <SelectTrigger data-testid="swap-colleague-select" className="mt-1.5 h-11">
                  <SelectValue placeholder={colleagues.length ? "Choose a colleague's shift" : "No eligible teammate shifts found"}/>
                </SelectTrigger>
                <SelectContent>
                  {colleagues.map(s => (
                    <SelectItem key={s.shift_id} value={s.shift_id}>
                      {s.name} · {fmtDate(s.work_date)} · {s.start_time}-{s.end_time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Reason (optional)</Label>
              <Textarea
                placeholder="A short note your colleague and admin will see"
                value={reason}
                onChange={e => setReason(e.target.value)}
                maxLength={240}
                rows={3}
                data-testid="swap-reason-input"
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                data-testid="swap-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={busy || !mineId || !collId}
                data-testid="swap-submit-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]">
                {busy ? "Sending…" : "Send swap request"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
